<script setup>
import { computed, ref } from 'vue';
import { settingsBreadcrumbs } from './breadcrumbs';

const props = defineProps({
    items: {
        type: Array,
        required: true,
    },
});

const breadcrumbs = computed(() => settingsBreadcrumbs());

const sectionOrder = ['account', 'company', 'website', 'workflows'];

const searchQuery = ref('');

const settingKeywords = {
    'company.user.edit': [
        'account',
        'avatar',
        'login',
        'my account',
        'my profile',
        'password',
        'personal settings',
        'profile',
        'user',
        'user account',
        'user settings',
    ],
    'settings.company.settings': [
        'billing',
        'company plan',
        'myflow pro',
        'plan',
        'plans',
        'price',
        'prices',
        'pricing',
        'subscription',
        'upgrade',
    ],
    'settings.company.whitelabel-app': [
        'app',
        'app settings',
        'branded app',
        'customer app',
        'mobile',
        'mobile app',
        'white label',
        'whitelabel',
        'whitelabeled app',
    ],
    'settings.company.invoicing.show': [
        'billing details',
        'default values',
        'faktura',
        'fakturering',
        'invoice',
        'invoices',
        'invoicing',
        'payment terms',
        'receipt',
        'receipts',
        'tax',
        'vat',
    ],
    'settings.company.settings.domains': [
        'custom domain',
        'dns',
        'domain',
        'domain name',
        'domains',
        'email domain',
        'host',
        'hosting',
        'url',
        'verify domain',
        'website domain',
    ],
    'crm.settings': [
        'contacts',
        'crm',
        'custom fields',
        'deal pipeline',
        'deal type',
        'deal types',
        'deals',
        'fields',
        'lead',
        'leads',
        'pipeline',
        'sales pipeline',
    ],
    'expert-contracts.settings.edit': [
        'agreement',
        'agreements',
        'contract',
        'contracts',
        'e-sign',
        'esign',
        'offer',
        'offers',
        'quote',
        'quotes',
        'signature',
        'signing',
    ],
    'coaching.profile': [
        'appointment',
        'appointments',
        'availability',
        'booking',
        'booking rules',
        'calendar',
        'cancellation',
        'coaching',
        'meeting',
        'meetings',
        'session',
        'sessions',
    ],
    'crm.communications.settings.show': [
        'communication',
        'delivery',
        'e mail',
        'email',
        'email delivery',
        'email sender',
        'email settings',
        'mail',
        'mandrill',
        'newsletter sender',
        'sender',
        'smtp',
    ],
    'settings.navigation.show': [
        'customer area navigation',
        'customer area menu',
        'kundzon',
        'menu',
        'nav',
        'navigation',
        'navigation items',
        'site menu',
        'website menu',
        'website navigation',
    ],
    'settings.footer.show': [
        'bottom',
        'footer',
        'front page',
        'frontpage',
        'homepage',
        'home page',
        'landing page',
        'start page',
        'startpage',
    ],
    'settings.appearance.show': [
        'appearance',
        'brand',
        'branding',
        'color',
        'colors',
        'colour',
        'favicon',
        'language',
        'logo',
        'look and feel',
        'theme',
        'visual',
    ],
    'settings.tracking.show': [
        'analytics',
        'conversion',
        'facebook pixel',
        'google analytics',
        'gtm',
        'insight tag',
        'linkedin insight',
        'meta pixel',
        'pixel',
        'tracking',
        'tracking id',
    ],
    'settings.policies.show': [
        'access',
        'account creation',
        'accounts',
        'admin',
        'admins',
        'delegated access',
        'gdpr',
        'policy',
        'policies',
        'privacy',
        'terms',
        'users',
    ],
    'modules.index': [
        'active features',
        'available features',
        'enable feature',
        'features',
        'modules',
        'optional features',
        'products',
        'turn on',
    ],
};

const normalizeSearchText = (value) =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/&/g, ' and ')
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .toLowerCase()
        .trim();

const searchTokens = computed(() =>
    normalizeSearchText(searchQuery.value).split(/\s+/).filter(Boolean)
);

const itemMatchesSearch = (item) => {
    if (!searchTokens.value.length) {
        return true;
    }

    const searchableValues = [
        item.routeName,
        item.section,
        _mft(item.name),
        _mft(item.description),
        ...(settingKeywords[item.routeName] || []),
    ];

    const searchableText = normalizeSearchText(searchableValues.join(' '));

    return searchTokens.value.every((token) => searchableText.includes(token));
};

const sections = computed(() => {
    const filteredItems = props.items.filter(itemMatchesSearch);

    const itemsBySection = filteredItems.reduce((sections, item) => {
        const section = item.section || 'other';

        sections[section] = sections[section] || [];
        sections[section].push(item);

        return sections;
    }, {});

    return [
        ...sectionOrder,
        ...Object.keys(itemsBySection).filter(
            (section) => !sectionOrder.includes(section)
        ),
    ]
        .filter((section) => itemsBySection[section]?.length)
        .map((section) => ({
            key: section,
            title: `navigation:backOffice.sidebar.settings.sections.${section}.title`,
            description: `navigation:backOffice.sidebar.settings.sections.${section}.description`,
            items: itemsBySection[section],
        }));
});

const resultCount = computed(() =>
    sections.value.reduce((total, section) => total + section.items.length, 0)
);

const hasSearchQuery = computed(() => searchTokens.value.length > 0);
</script>

<template>
    <div class="mx-auto w-full max-w-6xl space-y-6">
        <frosted-bar :breadcrumbs="breadcrumbs.length > 1 ? breadcrumbs : []">
            <div class="flex items-center justify-between space-x-4">
                <h1>
                    {{ _mft('navigation:backOffice.sidebar.settings.title') }}
                </h1>
            </div>
        </frosted-bar>

        <header class="flex flex-col items-center gap-4 pt-4 pb-8">
            <div class="w-full md:max-w-xl">
                <label for="settings-search" class="sr-only">
                    {{ _mft('shared:search') }}
                </label>
                <div class="relative">
                    <i
                        class="fa-regular fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400"
                    ></i>
                    <input
                        id="settings-search"
                        v-model="searchQuery"
                        type="search"
                        class="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-10 text-sm shadow-sm transition placeholder:text-gray-400 focus:border-brandPrimary-300 focus:outline-none focus:ring-2 focus:ring-brandPrimary-100"
                        :placeholder="
                            _mft(
                                'navigation:backOffice.sidebar.settings.searchPlaceholder'
                            )
                        "
                    />
                    <button
                        v-if="searchQuery"
                        type="button"
                        class="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                        :aria-label="_mft('shared:action.clear')"
                        :title="_mft('shared:action.clear')"
                        @click="searchQuery = ''"
                    >
                        <i class="fa-regular fa-xmark text-sm"></i>
                    </button>
                </div>
            </div>
        </header>

        <div v-if="resultCount" class="space-y-10">
            <section
                v-for="section in sections"
                :key="section.key"
                class="space-y-4"
            >
                <div class="max-w-3xl">
                    <h2 class="text-lg font-semibold text-gray-900">
                        {{ _mft(section.title) }}
                    </h2>
                    <p class="mt-1 text-sm leading-6 text-gray-600">
                        {{ _mft(section.description) }}
                    </p>
                </div>

                <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <a
                        v-for="item in section.items"
                        :key="item.routeName"
                        :href="item.href"
                        class="group flex h-full flex-col justify-between rounded-lg border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-brandPrimary-200 hover:shadow-md"
                        :class="
                            item.current
                                ? 'border-brandPrimary-300 ring-1 ring-brandPrimary-200'
                                : 'border-gray-200'
                        "
                    >
                        <div class="space-y-4">
                            <div class="flex items-start gap-4">
                                <span
                                    class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500 group-hover:bg-brandPrimary-50 group-hover:text-brandPrimary-500"
                                >
                                    <i :class="['fa-light', item.icon]"></i>
                                </span>
                                <div class="min-w-0">
                                    <h3
                                        class="text-base font-semibold text-gray-900"
                                    >
                                        {{ _mft(item.name) }}
                                    </h3>
                                    <p
                                        class="mt-1 text-sm leading-6 text-gray-600"
                                    >
                                        {{ _mft(item.description) }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center justify-end">
                            <span
                                class="mt-5 inline-flex items-center text-sm font-medium text-brandPrimary-600 group-hover:text-brandPrimary-700"
                            >
                                {{
                                    _mft(
                                        'navigation:backOffice.sidebar.settings.open'
                                    )
                                }}
                                <i
                                    class="fa-light fa-arrow-right ml-2 text-xs transition group-hover:translate-x-0.5"
                                ></i>
                            </span>
                        </div>
                    </a>
                </div>
            </section>
        </div>

        <div
            v-else-if="hasSearchQuery"
            class="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center"
        >
            <p class="text-sm font-medium text-gray-900">
                {{ _mft('shared:search.noResults') }}
            </p>
        </div>
    </div>
</template>
